import { ManageMetadataTableRow } from '@modules/integrationsV2/types';
import { MetadataDetailsViewFragment } from 'generated/graphql';
import { useCallback, useMemo, useRef, useState } from 'react';

interface ManageMetadataEditDrawerPaginationProps {
  metadataData: ManageMetadataTableRow[];
}

export const useManageMetadataEditDrawerPagination = ({
  metadataData,
}: ManageMetadataEditDrawerPaginationProps) => {
  const [metadataToEditKey, setMetadataToEditKey] = useState<string>();
  const [nextMetadataKey, setNextMetadataKey] = useState<string>();
  const [previousMetadataKey, setPreviousMetadataKey] = useState<string>();

  const tableRowIDs = useRef<string[]>([]);
  const idIndexMap = useRef<Map<string, number>>(new Map());

  const idRowMap = useMemo(
    () => new Map(metadataData.map((metadata) => [metadata.key, metadata])),
    [metadataData],
  );

  const onMetadataToEditChange = useCallback(
    (metadata: MetadataDetailsViewFragment) => {
      setMetadataToEditKey(metadata.key);
      setNextMetadataKey(undefined);
      setPreviousMetadataKey(undefined);

      const index = idIndexMap.current.get(metadata.key);
      if (index === undefined) {
        return;
      }
      if (index < tableRowIDs.current?.length) {
        const nextID = tableRowIDs.current[index + 1];
        setNextMetadataKey(nextID);
      }
      if (index > 0) {
        const previousID = tableRowIDs.current[index - 1];
        setPreviousMetadataKey(previousID);
      }
    },
    [],
  );

  const onCancel = useCallback(() => setMetadataToEditKey(undefined), []);

  const onRowsChange = useCallback((rowIDs: string[]) => {
    tableRowIDs.current = rowIDs;
    idIndexMap.current = new Map(rowIDs.map((id, index) => [id, index]));
  }, []);

  const metadataToEdit = idRowMap.get(metadataToEditKey ?? '');
  const nextMetadata = idRowMap.get(nextMetadataKey ?? '');
  const previousMetadata = idRowMap.get(previousMetadataKey ?? '');

  const onNext = useMemo(
    () =>
      nextMetadata ? () => onMetadataToEditChange(nextMetadata) : undefined,
    [nextMetadata, onMetadataToEditChange],
  );

  const onPrevious = useMemo(
    () =>
      previousMetadata
        ? () => onMetadataToEditChange(previousMetadata)
        : undefined,
    [onMetadataToEditChange, previousMetadata],
  );

  const currentFieldIndex = useMemo(
    () => idIndexMap.current.get(metadataToEditKey ?? '') ?? 0,
    [metadataToEditKey],
  );

  return {
    onMetadataToEditChange,
    onCancel,
    onRowsChange,
    onNext,
    onPrevious,
    metadataToEdit,
    currentFieldIndex,
  };
};
